import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ClipboardList, ChevronDown, Mail, RotateCcw, Plus } from 'lucide-react';
import { linkifyMail } from '../../lib/linkifyMail';
import { fadeUp } from '../../lib/dashboardMotion';
import {
  listNachforderungen,
  toggleItem as apiToggleItem,
  setNote as apiSetNote,
  setStatus as apiSetStatus,
  updateReview as apiUpdateReview,
  sortNachforderungen,
  nachforderungGruppe,
  ueberholteNachforderungen,
  ampelFor,
  type Nachforderung,
  type NachforderungItem,
} from '../../lib/nachforderungenApi';
import { ampelBadge, fmtDate, toDateInputValue } from './ampel';
import EigeneAufgabeEntry from './EigeneAufgabeEntry';
import NotizFeld from './NotizFeld';
import NeueAufgabeForm from './NeueAufgabeForm';
import {
  listEigeneAufgaben,
  createEigeneAufgabe,
  patchEigeneAufgabe,
  toggleEigenesItem,
  addEigenesItem,
  removeEigenesItem,
  setEigeneAufgabeStatus,
  deleteEigeneAufgabe,
  sortEigeneAufgaben,
  type EigeneAufgabe,
  type EigeneAufgabeItem,
  type NeueAufgabe,
} from '../../lib/eigeneAufgabenApi';
import {
  listAufgaben,
  aufgabeAbhaken,
  sortAufgaben,
  aufgabeZusatz,
  SCHRITT_HINWEIS,
  type OffeneAufgabe,
} from '../../lib/aufgabenApi';
import { teamsApi, type TeamMember } from '../../lib/teamsApi';


/**
 * Eine ältere Nachforderung zum selben Vorgang, die in der aktuellen Karte aufgegangen ist.
 *
 * Ihre Punkte stehen abgehakt da: Die BAFA hat sie in der neuen Runde nicht erneut
 * angemahnt — damit sind sie akzeptiert. Sie bleiben trotzdem sichtbar (samt Frist und
 * Original-Mail), damit nachvollziehbar bleibt, was wann gefordert wurde.
 */
function FruehereRunde({ r }: { r: Nachforderung }) {
  const [mailOffen, setMailOffen] = useState(false);
  return (
    <div data-testid="fruehere-runde" className="rounded-md bg-slate-50 dark:bg-slate-900/30 border border-slate-100 dark:border-slate-700/50 px-2.5 py-2">
      <div className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mb-1.5">
        Frühere Runde vom {fmtDate(r.createdAt)}
        {r.frist && <> · Frist {fmtDate(r.frist)}</>}
      </div>
      <ul className="space-y-1">
        {r.items.map((it) => (
          <li key={it.id} className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked readOnly aria-label={it.label} className="mt-0.5 w-3.5 h-3.5 text-slate-400 rounded" />
            <span className="line-through text-slate-400 dark:text-slate-500">{it.label}</span>
          </li>
        ))}
        {r.items.length === 0 && (
          <li className="text-xs text-slate-400 italic">Keine Punkte erfasst.</li>
        )}
      </ul>
      <button
        onClick={() => setMailOffen((v) => !v)}
        className="mt-1.5 text-[13px] font-medium text-slate-500 dark:text-slate-400 hover:underline"
      >
        Original-Mail
      </button>
      {mailOffen && (
        <pre className="mt-1 text-[13px] text-slate-500 dark:text-slate-400 whitespace-pre-wrap bg-white dark:bg-slate-900/40 rounded p-2 max-h-40 overflow-y-auto">
          {r.originalMailText ? linkifyMail(r.originalMailText) : 'Kein Mailtext gespeichert.'}
        </pre>
      )}
    </div>
  );
}

/**
 * Ein eigener Schritt aus der abgeleiteten Kette (kein BAFA-Einwurf).
 *
 * Aufklappbar wie die anderen Karten, aber ohne Häkchen: Es gibt nichts abzuhaken — der
 * Schritt endet mit dem Dokument, nicht mit einem Klick. Der aufgeklappte Bereich sagt,
 * welches Dokument das ist, seit wann die Sache liegt, und bietet den Erledigt-Knopf für
 * den Fall, dass es außerhalb des Systems passiert ist.
 */
function AufgabenZeile({
  a,
  mitglieder,
  onErledigt,
  onZusatz,
}: {
  a: OffeneAufgabe;
  mitglieder: TeamMember[];
  onErledigt: (a: OffeneAufgabe) => void;
  onZusatz: (a: OffeneAufgabe, patch: { frist?: string | null; notiz?: string | null; zustaendigUserId?: number | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const badge = a.frist ? ampelBadge(a.frist) : null;

  return (
    <div
      data-testid="aufgaben-zeile"
      className="rounded-lg border border-slate-200 dark:border-slate-700 hover:shadow-sm transition overflow-hidden"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
      >
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
              {a.kundeName || a.vorgangsnummer || <span className="text-slate-400 italic font-normal">Kunde</span>}
            </span>
            {a.kundeName && a.vorgangsnummer && (
              <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{a.vorgangsnummer}</span>
            )}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300 truncate mt-0.5">{a.label}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {a.zustaendigName && (
            <span className="text-[12px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200 whitespace-nowrap">
              {a.zustaendigName}
            </span>
          )}
          {badge && (
            <span className={`text-[12px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
              {badge.emoji} {badge.text}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-2 space-y-2.5 border-t border-slate-100 dark:border-slate-700/50">
          <p className="text-xs text-slate-600 dark:text-slate-400">{SCHRITT_HINWEIS[a.schritt]}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              Frist
              <input
                type="date"
                aria-label="Frist"
                value={toDateInputValue(a.frist)}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onZusatz(a, { frist: e.target.value || null })}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {/* Sagt, woher das Datum kommt — sonst weiß niemand, ob es abgesprochen ist. */}
              <span className="text-[11px] text-slate-400 dark:text-slate-500">
                {a.fristManuell ? 'gesetzt' : 'automatisch'}
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              Zuständig
              <select
                aria-label="Zuständig"
                value={a.zustaendigUserId != null ? String(a.zustaendigUserId) : ''}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onZusatz(a, { zustaendigUserId: e.target.value ? Number(e.target.value) : null })}
                className="px-2 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Niemand</option>
                {mitglieder.map((m) => (
                  <option key={m.id} value={m.id}>{m.contactPerson?.trim() || m.email}</option>
                ))}
              </select>
            </label>
          </div>

          <NotizFeld
            wert={a.notiz}
            placeholder="Notiz zu diesem Schritt…"
            onSpeichern={(v) => onZusatz(a, { notiz: v })}
          />

          <div className="flex items-center justify-end">
            <button
              onClick={() => onErledigt(a)}
              title={a.nurManuell ? 'Passiert im BAFA-Portal — hier abhaken' : 'Außerhalb des Systems erledigt? Hier abhaken.'}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-green-600 hover:bg-green-700 text-white"
            >
              Erledigt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Was diese Karte verlangt — die Zeile unter dem Kundennamen.
 *
 * Ohne Anzahl: Wie viele Punkte offen sind, sieht man beim Aufklappen an den Häkchen. Ohne
 * erkannte Punkte bleibt es bei der Herkunft — dann muss man die Mail lesen.
 */
function artDerKarte(n: Nachforderung): string {
  return n.items.length === 0 ? 'Nachforderung der BAFA' : 'Unterlagen nachreichen';
}

function NachEntry({
  n,
  fruehereRunden,
  onToggle,
  onNote,
  onConfirm,
  onErledigt,
  onFrist,
}: {
  n: Nachforderung;
  /** Ältere Nachforderungen zum selben Vorgang, die diese Karte aufgenommen hat
   *  (älteste zuerst). Ihre Punkte gelten als erledigt — die BAFA hat sie nicht erneut
   *  angemahnt — und werden hier nur noch nachgewiesen. */
  fruehereRunden?: Nachforderung[];
  onToggle: (n: Nachforderung, it: NachforderungItem) => void;
  onNote: (n: Nachforderung, value: string) => void;
  onConfirm: (n: Nachforderung) => void;
  onErledigt: (n: Nachforderung) => void;
  onFrist: (n: Nachforderung, value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const badge = ampelBadge(n.frist);
  // Alle nachgeforderten Punkte abgehakt → die Karte leuchtet grün. Das ist der Moment,
  // in dem der Vorgang wieder an die BAFA gehen kann; „Erledigt" bleibt der bewusste
  // letzte Klick, mit dem die Karte aus der Liste verschwindet.
  const alleErledigt = n.items.length > 0 && n.items.every((it) => it.done);
  const runden = fruehereRunden ?? [];

  const handleHeaderClick = () => {
    const willOpen = !open;
    setOpen(willOpen);
    // Öffnen = „gesehen/geprüft" → „Neu · bitte prüfen" verschwindet (Status neu → bestätigt).
    if (willOpen && n.status === 'neu') onConfirm(n);
  };

  return (
    <div
      data-testid="nf-card"
      data-vollstaendig={alleErledigt ? 'ja' : 'nein'}
      data-runden={runden.length + 1}
      className={`rounded-lg border hover:shadow-sm transition overflow-hidden ${
        alleErledigt
          ? 'border-green-300 bg-green-50/60 dark:border-green-800 dark:bg-green-900/10'
          : 'border-slate-200 dark:border-slate-700'
      }`}
    >
      <div
        data-testid="nf-header"
        role="button"
        tabIndex={0}
        onClick={handleHeaderClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHeaderClick(); } }}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
      >
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        {/* Links: Kundenname + Vorgangsnummer, darunter WAS zu tun ist. Die Kontaktdaten
            stehen aufgeklappt — beim Überfliegen der Liste will man wissen, worum es geht,
            nicht wen man anrufen könnte. */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
              {n.kundeName || n.vorgangsnummer || <span className="text-slate-400 italic font-normal">Kunde</span>}
            </span>
            {n.kundeName && n.vorgangsnummer && (
              <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{n.vorgangsnummer}</span>
            )}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300 truncate mt-0.5">{artDerKarte(n)}</div>
        </div>
        {/* Rechts: Badges (oben), zuletzt bearbeitet (unten) */}
        <div className="ml-auto flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-2">
            {alleErledigt && (
              <span className="text-[12px] font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 whitespace-nowrap">
                ✓ Vollständig
              </span>
            )}
            {!alleErledigt && n.status === 'neu' && (
              <span className="text-[12px] font-medium text-red-600 dark:text-red-400">
                Neu
              </span>
            )}
            {/* Frist-Ampel nur, solange noch etwas offen ist. Ist alles abgehakt, wäre ein
                rotes „Überfällig" neben dem grünen „✓ Vollständig" ein Widerspruch: Geliefert
                ist geliefert. Die Frist bleibt aufgeklappt im Datumsfeld einsehbar. */}
            {!alleErledigt && (
              <span className={`text-[12px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
                {badge.emoji} {badge.text}
              </span>
            )}
          </div>
          {fmtDate(n.updatedAt) && (
            <span title="zuletzt bearbeitet" className="text-[12px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
              bearb. {fmtDate(n.updatedAt)}
            </span>
          )}
        </div>
      </div>

      {open && (
        <div className="px-3 pb-4 pt-2 space-y-3 border-t border-slate-100 dark:border-slate-700/50">
          {(n.kundeAnsprechpartner || n.kundeTelefon) && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {n.kundeAnsprechpartner}
              {n.kundeAnsprechpartner && n.kundeTelefon && ' · '}
              {n.kundeTelefon && (
                <a
                  href={`tel:${n.kundeTelefon.replace(/\s+/g, '')}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                >
                  {n.kundeTelefon}
                </a>
              )}
            </div>
          )}
          <ul className="space-y-1.5">
            {n.items.length === 0 && (
              <li className="text-xs text-slate-500 dark:text-slate-400 italic">
                Keine Punkte erkannt — bitte Mail prüfen und Punkte ergänzen.
              </li>
            )}
            {n.items.map((it) => (
              <li key={it.id} className="text-sm">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    aria-label={it.label}
                    checked={it.done}
                    onChange={() => onToggle(n, it)}
                    className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className={`flex-1 ${it.done ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>
                    {it.label}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {runden.map((r) => (
            <FruehereRunde key={r.id} r={r} />
          ))}

          <button
            onClick={() => setShowMail((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            <Mail className="w-3.5 h-3.5" /> Original-Mail anzeigen
          </button>
          {showMail && (
            <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-900/40 rounded-md p-2 max-h-48 overflow-y-auto">
              {n.originalMailText ? linkifyMail(n.originalMailText) : 'Kein Mailtext gespeichert.'}
            </pre>
          )}

          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
            Frist
            <input
              type="date"
              aria-label="Frist"
              value={toDateInputValue(n.frist)}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onFrist(n, e.target.value || null)}
              className="px-2 py-1 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {/* Fall Ionis/1307141: die Mail nannte 23.10., der Berater setzte nach einer
                telefonischen Verlängerung manuell 06.11. — ohne diesen Hinweis sieht die
                "Original-Mail anzeigen"-Ansicht später widersprüchlich aus. */}
            {n.fristUrspruenglich && toDateInputValue(n.fristUrspruenglich) !== toDateInputValue(n.frist) && (
              <span
                className="text-amber-600 dark:text-amber-400"
                title={`Manuell geändert — in der Mail stand ursprünglich ${fmtDate(n.fristUrspruenglich)}`}
              >
                manuell geändert (urspr. {fmtDate(n.fristUrspruenglich)})
              </span>
            )}
          </label>

          <NotizFeld
            wert={n.note}
            placeholder="Notiz zu dieser Nachforderung…"
            onSpeichern={(v) => onNote(n, v)}
          />

          <div className="flex items-center justify-end">
            <button
              onClick={() => onErledigt(n)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-green-600 hover:bg-green-700 text-white"
            >
              Erledigt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NachforderungenBlock(props: {
  darkMode?: boolean;
  /** Meldet die Anzahl „Überfällig"-markierter Karten nach oben — für den Begrüßungskopf
   *  im Dashboard (Kundenfeedback 16.10.2026: Grußtext zeigte „alles im grünen Bereich",
   *  obwohl hier 5 rote Karten standen, weil er nur Förderzeitraum/BAFA-Rückfragen kannte,
   *  nicht die eigenen/abgeleiteten Aufgaben aus diesem Block). */
  onCountsChange?: (counts: { total: number; ueberfaellig: number }) => void;
}) {
  const [items, setItems] = useState<Nachforderung[] | null>(null);
  const [undo, setUndo] = useState<Nachforderung | null>(null);
  // Eigene Schritte — abgeleitet, siehe docs/aufgaben.md. Fällt der Abruf aus, bleibt der
  // Nachforderungs-Teil des Blocks trotzdem benutzbar.
  const [aufgaben, setAufgaben] = useState<OffeneAufgabe[]>([]);
  // Selbst angelegte Aufgaben — dieselbe Karte wie eine Nachforderung, andere Herkunft.
  const [eigene, setEigene] = useState<EigeneAufgabe[]>([]);
  const [formOffen, setFormOffen] = useState(false);
  /** Für die Zuständig-Auswahl in beiden Kartenarten. */
  const [mitglieder, setMitglieder] = useState<TeamMember[]>([]);
  /** Gerade erledigte eigene Aufgabe für „Rückgängig" (wie bei den Nachforderungen). */
  const [undoEigen, setUndoEigen] = useState<EigeneAufgabe | null>(null);
  const prefersReducedMotion = useReducedMotion();

  /** Gerade abgehakte Schritte. Bis der Server sie kennt, würde ein Neuabruf sie
   *  zurückbringen — die Zeile bliebe „erledigt, aber sichtbar". */
  const abgehakt = useRef(new Set<string>());
  const aufgabenKey = (a: OffeneAufgabe) => `${a.vorgangId}-${a.schritt}`;

  const ladeAufgaben = useCallback(() => {
    listAufgaben()
      .then((a) => setAufgaben(sortAufgaben(a.filter((x) => !abgehakt.current.has(aufgabenKey(x))))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listNachforderungen()
      .then(setItems)
      .catch(() => setItems([]));
    listEigeneAufgaben()
      .then((l) => setEigene(sortEigeneAufgaben(l)))
      .catch(() => setEigene([]));
    teamsApi.getMyTeam().then((t) => setMitglieder(t.members ?? [])).catch(() => setMitglieder([]));
    ladeAufgaben();
  }, [ladeAufgaben]);

  // Erledigt wird woanders: Der Bericht entsteht im Editor, die Unterschrift kommt per
  // Upload herein. Kehrt der Blick aufs Dashboard zurück, muss der Schritt weg sein —
  // sonst steht dort eine Aufgabe, die der Nutzer gerade selbst abgeschlossen hat.
  useEffect(() => {
    const beiRueckkehr = () => {
      if (document.visibilityState === 'visible') ladeAufgaben();
    };
    window.addEventListener('focus', beiRueckkehr);
    document.addEventListener('visibilitychange', beiRueckkehr);
    return () => {
      window.removeEventListener('focus', beiRueckkehr);
      document.removeEventListener('visibilitychange', beiRueckkehr);
    };
  }, [ladeAufgaben]);

  const onAufgabeZusatz = (
    a: OffeneAufgabe,
    patch: { frist?: string | null; notiz?: string | null; zustaendigUserId?: number | null },
  ) => {
    const vorher = { frist: a.frist, notiz: a.notiz, zustaendigUserId: a.zustaendigUserId };
    setAufgaben((prev) =>
      prev.map((x) =>
        x.vorgangId === a.vorgangId && x.schritt === a.schritt
          ? {
              ...x,
              ...patch,
              // Ein gesetztes Datum bleibt gesetzt, ein geleertes fällt auf die Ableitung
              // zurück — die kennt nur der Server, deshalb lädt der Neuabruf sie nach.
              fristManuell: patch.frist !== undefined ? !!patch.frist : x.fristManuell,
              zustaendigName:
                patch.zustaendigUserId !== undefined
                  ? (mitglieder.find((m) => m.id === patch.zustaendigUserId)?.contactPerson?.trim() ?? null)
                  : x.zustaendigName,
            }
          : x,
      ),
    );
    aufgabeZusatz(a.vorgangId, a.schritt, patch)
      .then(() => { if (patch.frist === null) ladeAufgaben(); })
      .catch(() => {
        setAufgaben((prev) =>
          prev.map((x) => (x.vorgangId === a.vorgangId && x.schritt === a.schritt ? { ...x, ...vorher } : x)),
        );
      });
  };

  const onAufgabeErledigt = (a: OffeneAufgabe) => {
    abgehakt.current.add(aufgabenKey(a));
    setAufgaben((prev) => prev.filter((x) => aufgabenKey(x) !== aufgabenKey(a)));
    aufgabeAbhaken(a.vorgangId, a.schritt).catch(() => {
      // Fehlgeschlagen → Zeile zurückholen, damit nichts still verschwindet.
      abgehakt.current.delete(aufgabenKey(a));
      setAufgaben((prev) => sortAufgaben([...prev, a]));
    });
  };

  /** Ersetzt eine eigene Aufgabe durch die Serverantwort — die ist die Wahrheit. */
  const ersetzeEigene = (a: EigeneAufgabe) =>
    setEigene((prev) => sortEigeneAufgaben(prev.map((x) => (x.id === a.id ? a : x))));

  const onEigeneAnlegen = async (input: NeueAufgabe) => {
    const neu = await createEigeneAufgabe(input);
    setEigene((prev) => sortEigeneAufgaben([neu, ...prev]));
    setFormOffen(false);
  };

  const onEigenesItem = (a: EigeneAufgabe, it: EigeneAufgabeItem) => {
    const done = !it.done;
    // Sofort umschalten, danach bestätigen lassen: Ein Häkchen darf nicht auf das Netz warten.
    setEigene((prev) => prev.map((x) => (x.id === a.id ? { ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, done } : i)) } : x)));
    toggleEigenesItem(a.id, it.id, done).then(ersetzeEigene).catch(() => {
      setEigene((prev) => prev.map((x) => (x.id === a.id ? { ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, done: it.done } : i)) } : x)));
    });
  };

  const onEigenerPunktHinzu = (a: EigeneAufgabe, label: string) => {
    addEigenesItem(a.id, label).then(ersetzeEigene).catch(() => {});
  };

  const onEigenerPunktWeg = (a: EigeneAufgabe, itemId: string) => {
    removeEigenesItem(a.id, itemId).then(ersetzeEigene).catch(() => {});
  };

  const onEigeneFrist = (a: EigeneAufgabe, value: string | null) => {
    setEigene((prev) => sortEigeneAufgaben(prev.map((x) => (x.id === a.id ? { ...x, frist: value } : x))));
    patchEigeneAufgabe(a.id, { frist: value }).then(ersetzeEigene).catch(() => {
      setEigene((prev) => sortEigeneAufgaben(prev.map((x) => (x.id === a.id ? { ...x, frist: a.frist } : x))));
    });
  };

  const onEigeneNotiz = (a: EigeneAufgabe, value: string) => {
    if (value === (a.notiz ?? '')) return;
    setEigene((prev) => prev.map((x) => (x.id === a.id ? { ...x, notiz: value } : x)));
    patchEigeneAufgabe(a.id, { notiz: value }).then(ersetzeEigene).catch(() => {});
  };

  const onEigeneZustaendig = (a: EigeneAufgabe, userId: number | null) => {
    setEigene((prev) => prev.map((x) => (x.id === a.id ? { ...x, zustaendigUserId: userId } : x)));
    patchEigeneAufgabe(a.id, { zustaendigUserId: userId }).then(ersetzeEigene).catch(() => {
      setEigene((prev) => prev.map((x) => (x.id === a.id ? { ...x, zustaendigUserId: a.zustaendigUserId } : x)));
    });
  };

  const onEigeneErledigt = (a: EigeneAufgabe) => {
    setEigene((prev) => prev.filter((x) => x.id !== a.id));
    setUndoEigen(a);
    setEigeneAufgabeStatus(a.id, 'erledigt').catch(() => {
      setEigene((prev) => (prev.some((x) => x.id === a.id) ? prev : sortEigeneAufgaben([...prev, a])));
      setUndoEigen((u) => (u?.id === a.id ? null : u));
    });
  };

  const undoEigeneErledigt = () => {
    if (!undoEigen) return;
    const a = undoEigen;
    setUndoEigen(null);
    setEigene((prev) => (prev.some((x) => x.id === a.id) ? prev : sortEigeneAufgaben([...prev, a])));
    setEigeneAufgabeStatus(a.id, 'offen').catch(() => {});
  };

  const onEigeneLoeschen = (a: EigeneAufgabe) => {
    // Löschen ist endgültig — deshalb kein „Rückgängig", sondern eine Rückfrage.
    if (!window.confirm(`Aufgabe „${a.titel}" wirklich löschen?`)) return;
    setEigene((prev) => prev.filter((x) => x.id !== a.id));
    deleteEigeneAufgabe(a.id).catch(() => {
      setEigene((prev) => sortEigeneAufgaben([...prev, a]));
    });
  };

  // Mehrere Runden zum selben Vorgang: Die neueste Karte nimmt die älteren auf; als eigene
  // Karte erscheinen die nicht mehr. Sonst stünde eine abgelaufene Frist ganz oben, deren
  // Punkte die BAFA längst akzeptiert hat.
  const aufgenommen = useMemo(() => ueberholteNachforderungen(items ?? []), [items]);
  const sorted = useMemo(
    () => sortNachforderungen((items ?? []).filter((n) => !aufgenommen.has(n.id))),
    [items, aufgenommen],
  );

  /** Eine gemeinsame Reihenfolge über alle drei Quellen (BAFA-Nachforderungen, eigene
   *  Aufgaben, abgeleitete Kette) — sonst stapeln sich die Blöcke unabhängig voneinander
   *  sortiert und eine dringende eigene Aufgabe steht hinter einer längst erledigten
   *  Nachforderung. Gleiche Rang-Logik wie sortNachforderungen (ampelFor), nur über alle
   *  Quellen hinweg; vollständig abgehakt (aber noch nicht geschlossen) rutscht ganz nach
   *  unten, der Rest nach Fälligkeitsdatum aufsteigend. */
  const alleSortiert = useMemo(() => {
    const vollstaendigItems = (liste?: { done: boolean }[]) => !!liste && liste.length > 0 && liste.every((it) => it.done);
    type Eintrag =
      | { typ: 'nachforderung'; key: string; frist: string | null; vollstaendig: boolean; n: Nachforderung }
      | { typ: 'eigene'; key: string; frist: string | null; vollstaendig: boolean; a: EigeneAufgabe }
      | { typ: 'aufgabe'; key: string; frist: string | null; vollstaendig: boolean; a: OffeneAufgabe };
    const eintraege: Eintrag[] = [
      ...sorted.map((n): Eintrag => ({ typ: 'nachforderung', key: `n-${n.id}`, frist: n.frist, vollstaendig: vollstaendigItems(n.items), n })),
      ...eigene.map((a): Eintrag => ({ typ: 'eigene', key: `e-${a.id}`, frist: a.frist, vollstaendig: vollstaendigItems(a.items), a })),
      ...aufgaben.map((a): Eintrag => ({ typ: 'aufgabe', key: `a-${a.vorgangId}-${a.schritt}`, frist: a.frist, vollstaendig: false, a })),
    ];
    const rang = (e: Eintrag): number => {
      if (e.vollstaendig) return 3;
      if (ampelFor(e.frist) === 'rot') return 0;
      return e.frist ? 2 : 1;
    };
    return eintraege.sort((x, y) => {
      const rx = rang(x);
      const ry = rang(y);
      if (rx !== ry) return rx - ry;
      const fx = x.frist ? new Date(x.frist).getTime() : Infinity;
      const fy = y.frist ? new Date(y.frist).getTime() : Infinity;
      return fx - fy;
    });
  }, [sorted, eigene, aufgaben]);

  const onCountsChangeRef = useRef(props.onCountsChange);
  onCountsChangeRef.current = props.onCountsChange;
  useEffect(() => {
    const rang = (e: { vollstaendig: boolean; frist: string | null }): number => {
      if (e.vollstaendig) return 3;
      if (ampelFor(e.frist) === 'rot') return 0;
      return e.frist ? 2 : 1;
    };
    const ueberfaellig = alleSortiert.filter((e) => rang(e) === 0).length;
    onCountsChangeRef.current?.({ total: alleSortiert.length, ueberfaellig });
  }, [alleSortiert]);
  /** Zu einer sichtbaren Karte: die von ihr aufgenommenen früheren Runden, älteste zuerst.
   *  Dieselbe Gruppierung wie ueberholteNachforderungen — sonst würde eine eingeklappte
   *  Karte aus der Liste fallen, ohne in ihrer Nachfolgerin aufzutauchen. */
  const fruehereRundenVon = (n: Nachforderung) => {
    const key = nachforderungGruppe(n);
    if (key == null) return [];
    return (items ?? [])
      .filter((x) => aufgenommen.has(x.id) && nachforderungGruppe(x) === key)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  };

  const onToggle = (n: Nachforderung, it: NachforderungItem) => {
    const done = !it.done;
    setItems((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, done } : i)) } : x)));
    apiToggleItem(n.id, it.id, done).catch(() => {
      setItems((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, done: it.done } : i)) } : x)));
    });
  };

  const onNote = (n: Nachforderung, value: string) => {
    if (value === (n.note ?? '')) return;
    setItems((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, note: value } : x)));
    apiSetNote(n.id, value).catch(() => {});
  };

  const onFrist = (n: Nachforderung, value: string | null) => {
    const prev = n.frist;
    setItems((p) => (p ?? []).map((x) => (x.id === n.id ? { ...x, frist: value } : x)));
    apiUpdateReview(n.id, { frist: value }).catch(() => {
      setItems((p) => (p ?? []).map((x) => (x.id === n.id ? { ...x, frist: prev } : x)));
    });
  };

  const onConfirm = (n: Nachforderung) => {
    setItems((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, status: 'bestaetigt' } : x)));
    apiSetStatus(n.id, 'bestaetigt').catch(() => {
      setItems((prev) => (prev ?? []).map((x) => (x.id === n.id ? { ...x, status: 'neu' } : x)));
    });
  };

  const onErledigt = (n: Nachforderung) => {
    // Die aufgenommenen früheren Runden mit schließen: Sie sind in dieser Karte
    // aufgegangen und würden sonst als eigene (wieder neueste) Karte zurückkommen.
    const mitgeschlossen = fruehereRundenVon(n);
    for (const r of mitgeschlossen) apiSetStatus(r.id, 'erledigt').catch(() => {});
    setItems((prev) => (prev ?? []).filter((x) => x.id !== n.id && !mitgeschlossen.some((r) => r.id === x.id)));
    setUndo(n);
    apiSetStatus(n.id, 'erledigt').catch(() => {
      // Idempotent re-adden: falls der Eintrag zwischenzeitlich schon (z.B. per „Rückgängig")
      // zurückgeholt wurde, NICHT doppelt anhängen (sonst doppelter React-key).
      setItems((prev) => ((prev ?? []).some((x) => x.id === n.id) ? (prev ?? []) : [...(prev ?? []), n]));
      setUndo((prevUndo) => (prevUndo?.id === n.id ? null : prevUndo));
    });
  };

  const doUndo = () => {
    if (!undo) return;
    const n = undo;
    setUndo(null);
    setItems((prev) => ((prev ?? []).some((x) => x.id === n.id) ? (prev ?? []) : [...(prev ?? []), n]));
    apiSetStatus(n.id, n.status).catch(() => {});
  };

  return (
    <motion.div variants={fadeUp(prefersReducedMotion)} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4 sm:p-6 flex flex-col">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2 truncate">Zu erledigen</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Nachforderungen der BAFA und Ihre eigenen nächsten Schritte.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {sorted.length + aufgaben.length + eigene.length > 0 && (
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-full px-2.5 py-1">
              {sorted.length + aufgaben.length + eigene.length}
            </span>
          )}
          {/* Gleiche Größe und Machart wie „Neuer Bericht" und „Neuer Kunde", nur in
              Bernstein — der Farbe des Kachel-Symbols. */}
          <button
            onClick={() => setFormOffen((v) => !v)}
            aria-label="Neue Aufgabe"
            title="Neue Aufgabe"
            className="w-12 h-12 aspect-square flex items-center justify-center bg-amber-600 dark:bg-amber-500 text-white rounded-lg hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors shadow-sm hover:shadow-md cursor-pointer"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 flex flex-col flex-1">
      {undo && (
        <div className="flex items-center justify-between gap-2 mb-3 text-xs rounded-lg bg-slate-100 dark:bg-slate-700/50 px-3 py-2">
          <span className="text-slate-600 dark:text-slate-300">Als erledigt markiert.</span>
          <button onClick={doUndo} className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:underline">
            <RotateCcw className="w-3.5 h-3.5" /> Rückgängig
          </button>
        </div>
      )}

      {undoEigen && (
        <div className="flex items-center justify-between gap-2 mb-3 text-xs rounded-lg bg-slate-100 dark:bg-slate-700/50 px-3 py-2">
          <span className="text-slate-600 dark:text-slate-300">Aufgabe erledigt.</span>
          <button onClick={undoEigeneErledigt} className="inline-flex items-center gap-1 font-medium text-blue-600 dark:text-blue-400 hover:underline">
            <RotateCcw className="w-3.5 h-3.5" /> Rückgängig
          </button>
        </div>
      )}

      {formOffen && (
        <NeueAufgabeForm onAnlegen={onEigeneAnlegen} onAbbrechen={() => setFormOffen(false)} />
      )}

      {items === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">Lädt…</p>
      ) : sorted.length === 0 && aufgaben.length === 0 && eigene.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">Nichts offen — alles erledigt.</p>
      ) : (
        // Eine Liste, keine Abschnitte: Nachforderungen, eigene Schritte und die abgeleitete
        // Kette sind beides dasselbe — was heute zu tun ist. Sortierung ueber alle drei
        // Quellen hinweg (alleSortiert): vollstaendig abgehakt ganz unten, sonst nach
        // Faelligkeitsdatum, Ueberfaelliges zuerst.
        <div className="mt-1 space-y-2 max-h-[30rem] overflow-y-auto pr-1">
          {alleSortiert.map((e) => {
            if (e.typ === 'nachforderung') {
              return (
                <NachEntry key={e.key} n={e.n} fruehereRunden={fruehereRundenVon(e.n)} onToggle={onToggle} onNote={onNote} onConfirm={onConfirm} onErledigt={onErledigt} onFrist={onFrist} />
              );
            }
            if (e.typ === 'eigene') {
              return (
                <EigeneAufgabeEntry
                  key={e.key}
                  a={e.a}
                  mitglieder={mitglieder}
                  onToggleItem={onEigenesItem}
                  onPunktHinzu={onEigenerPunktHinzu}
                  onPunktWeg={onEigenerPunktWeg}
                  onFrist={onEigeneFrist}
                  onNotiz={onEigeneNotiz}
                  onErledigt={onEigeneErledigt}
                  onLoeschen={onEigeneLoeschen}
                  onZustaendig={onEigeneZustaendig}
                />
              );
            }
            return (
              <AufgabenZeile
                key={e.key}
                a={e.a}
                mitglieder={mitglieder}
                onErledigt={onAufgabeErledigt}
                onZusatz={onAufgabeZusatz}
              />
            );
          })}
        </div>
      )}
      </div>
    </motion.div>
  );
}
